import { useState } from 'react';

import { MISSING } from 'common/helpers/format';
import ModalApp from 'components/modalApp';
import Value from 'components/value';

import * as S from './styles';

interface IProps {
  /** The answer this respondent gave, or nothing if they skipped the question. */
  answer?: string;
  /** The question, used as the dialog's head. */
  question: string;
  /** The respondent, so the dialog still says whose answer it is showing. */
  respondent?: string;
}

/**
 * Where an answer stops being a table value and becomes something to read.
 *
 * The answer columns are a fixed 240px and a cell is one line of 13px UI text
 * — call it thirty-five Latin characters, fewer for wider glyphs. Anything
 * past this gets the control, which errs deliberately early: offering it on an
 * answer that happened to fit costs a reader nothing, and withholding it on
 * one that did not is the bug being fixed. Below it, the plain value keeps
 * AppTable's own treatment — clipped with an ellipsis, full text on hover.
 */
const READABLE_IN_A_CELL = 32;

const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2v-4M15 3h4a2 2 0 012 2v4M21 15v4a2 2 0 01-2 2h-4M3 9V5a2 2 0 012-2h4" />
  </svg>
);

/**
 * One answer in the responses table.
 *
 * A "why?" question gets paragraphs, and the cell holding one is a single
 * clipped line — so the interesting half of the answer was an ellipsis with
 * nothing behind it. AppTable does put a clipped value on the cell's `title`,
 * but a native tooltip is a poor place to read a paragraph, it cannot be
 * selected or copied, and on a touch screen it never appears at all. A long
 * answer opens in a dialog instead, titled with the question it answers.
 */
export default function SurveyAnswerCell({ answer, question, respondent }: IProps) {
  const [show, setShow] = useState(false);

  if (!answer) return <Value fallback={MISSING.none} />;

  if (answer.length <= READABLE_IN_A_CELL) return <>{answer}</>;

  return (
    <>
      <S.Trigger type="button" onClick={() => setShow(true)} title="Show the full answer">
        <S.Text className="answer-text">{answer}</S.Text>
        <ExpandIcon />
      </S.Trigger>

      <ModalApp show={show} handleClose={() => setShow(false)} fixed center title={question}>
        {respondent ? <S.Respondent>{respondent}</S.Respondent> : null}
        <S.Answer>{answer}</S.Answer>
      </ModalApp>
    </>
  );
}
